import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  Alert,
  CircularProgress,
  LinearProgress,
  Paper,
  ListItemIcon,
} from "@mui/material";
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
} from "@mui/icons-material";
import Cron from "react-cron-generator";
import "react-cron-generator/build/cron-builder.css";
import CronBuilderBox from "../../../components/CronBuilderBox";
import CronStatusPanel from "../../../components/CronStatusPanel";

import { plentyArticlesApi } from "../../../services/api/plenty/articles";
import { createPlentyWebSocket } from "../../../services/api/plenty/websocket";
import { plentyCredentialsApi } from "../../../services/api/plenty/plenty_credentials";
import { sheetsSettingsApi } from "../../../services/api/plenty/sheets_settings";
import { googleApi } from "../../../services/api/plenty/google_credentials";

const PopulateArticlesTab = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Cron Job state
  const [cronStatus, setCronStatus] = useState(null);
  const [cronExpression, setCronExpression] = useState("0 0 2 * * *"); // Default: 2 AM daily
  const [cronLoading, setCronLoading] = useState(false);

  // Populate Articles state
  const [populateLoading, setPopulateLoading] = useState(false);
  const [populateResult, setPopulateResult] = useState(null);
  const [populateResults, setPopulateResults] = useState(null);

  // System status state
  const [systemStatus, setSystemStatus] = useState({
    isReady: false,
    hasActiveGoogleCredential: false,
    hasValidSheetSettings: false,
    hasActivePlentyCredential: false,
  });

  // Download states
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadStatus, setDownloadStatus] = useState("idle"); // 'idle', 'running', 'completed', 'error'
  const [downloadStatusMessage, setDownloadStatusMessage] = useState("");
  const [downloadStats, setDownloadStats] = useState({
    total: 0,
    processed: 0,
    successful: 0,
    failed: 0,
    currentStep: "",
  });
  const [websocket, setWebsocket] = useState(null);

  // Helper function for error messages
  const getErrorMessage = (error) => {
    if (error.response) {
      return (
        error.response.data?.detail ||
        error.response.data?.message ||
        "Server error occurred"
      );
    }
    return error.message || "An unexpected error occurred";
  };

  // Check system status on mount
  useEffect(() => {
    checkSystemStatus();
  }, []);

  // Check populate status on page load and initialize WebSocket if process is running
  useEffect(() => {
    const checkPopulateStatus = async () => {
      try {
        const response = await plentyArticlesApi.getPopulateStatus();
        const status = response.data;

        if (status.is_running) {
          console.log("Populate process is running, restoring state:", status);

          // Restore progress state
          setDownloadStatus(status.status);
          setDownloadProgress(status.progress);
          setDownloadStatusMessage(status.message);
          setDownloadStats(status.stats);

          // Initialize WebSocket to receive live updates
          if (!websocket) {
            console.log("Initializing WebSocket for active populate process");
            initializeWebSocket();
          }
        }
      } catch (error) {
        console.error("Failed to check populate status:", error);
        // Don't show error to user, just log it
      }
    };

    checkPopulateStatus();
  }, []);

  useEffect(() => {
    return () => {
      if (websocket) {
        console.log("Cleaning up WebSocket on component unmount");
        websocket.close();
        setWebsocket(null);
      }
    };
  }, [websocket]);

  // ws
  const initializeWebSocket = () => {
    const handleMessage = (data) => {
      console.log("Handling WebSocket message:", data);
      switch (data.type) {
        case "progress":
          console.log("Processing progress update:", data.progress);
          // Apply progress updates directly - no throttling
          setDownloadProgress(data.progress);
          setDownloadStats(data.stats);
          setDownloadStatusMessage(data.message);

          // Reset status to idle when progress reaches 100%
          if (data.progress >= 100) {
            setDownloadStatus("idle");
            setPopulateLoading(false);
          }
          break;
        case "status":
          console.log("Processing status update:", data.status);
          setDownloadStatus(data.status);
          setDownloadStatusMessage(data.message);
          if (data.status === "completed") {
            setPopulateLoading(false);
          }
          break;
        case "completed":
          console.log("Processing completion message");
          setDownloadStatus("idle"); // Reset to idle so the button shows "Start Download" again
          setDownloadProgress(100);
          setDownloadStatusMessage(data.message);
          setSuccess("Download completed successfully!");
          setPopulateLoading(false);
          break;
        case "error":
          console.log("Processing error message:", data.message);
          setDownloadStatus("error");
          setDownloadStatusMessage(data.message);
          setError(`Download failed: ${data.message}`);
          setPopulateLoading(false);
          break;
        default:
          console.log("Unknown message type:", data.type, data);
      }
    };

    const handleError = (error) => {
      console.error("WebSocket error:", error);
      setError("WebSocket connection failed");
    };

    const ws = createPlentyWebSocket(handleMessage, handleError);

    if (!ws) {
      // WebSocket failed to create (likely due to proxy/URL issues). Log and continue.
      console.warn(
        "WebSocket could not be established. Continuing without live progress updates.",
      );
      setWebsocket(null);
      return null;
    }

    // Only attach handlers if ws was created successfully
    try {
      ws.onopen = () => {
        console.log("WebSocket connected");
        setWebsocket(ws);
      };

      ws.onclose = () => {
        console.log("WebSocket disconnected");
        setWebsocket(null);
      };
    } catch (err) {
      console.error(
        "Error attaching WebSocket handlers, continuing without WS:",
        err,
      );
      // Ensure we don't leave a partially initialized socket
      try {
        ws.close();
      } catch (e) {}
      setWebsocket(null);
      return null;
    }

    return ws;
  };

  const checkSystemStatus = async () => {
    try {
      // Check for active Google credential
      const googleResponse = await googleApi.getCredentials();
      const googleCredentials = googleResponse.data || [];
      const hasActiveGoogleCredential = googleCredentials.some(
        (cred) => cred.active_plenty === true && cred.status === "authorized",
      );

      // Check for active Plenty credential
      const plentyResponse = await plentyCredentialsApi.getCredentials();
      const plentyCredentials = plentyResponse.data || [];
      const hasActivePlentyCredential = plentyCredentials.some(
        (cred) => cred.active === true,
      );

      // Check for valid sheet settings (check any of the sheet types)
      let hasValidSheetSettings = false;
      try {
        const [amazonSettings, plentySettings, csvSettings] = await Promise.all(
          [
            sheetsSettingsApi.getSettings("amazon"),
            sheetsSettingsApi.getSettings("plenty"),
            sheetsSettingsApi.getSettings("csv"),
          ],
        );

        // Valid if at least one sheet has a spreadsheet_id configured
        hasValidSheetSettings = !!(
          amazonSettings.data?.spreadsheet_id ||
          plentySettings.data?.spreadsheet_id ||
          csvSettings.data?.spreadsheet_id
        );
      } catch (error) {
        console.error("Failed to load sheet settings:", error);
      }

      const isReady =
        hasActiveGoogleCredential &&
        hasActivePlentyCredential &&
        hasValidSheetSettings;

      setSystemStatus({
        isReady,
        hasActiveGoogleCredential,
        hasValidSheetSettings,
        hasActivePlentyCredential,
      });

      return {
        isReady,
        hasActiveGoogleCredential,
        hasValidSheetSettings,
        hasActivePlentyCredential,
      };
    } catch (error) {
      console.error("Failed to check system status:", error);
      setSystemStatus({
        isReady: false,
        hasActiveGoogleCredential: false,
        hasValidSheetSettings: false,
        hasActivePlentyCredential: false,
      });
      return {
        isReady: false,
        hasActiveGoogleCredential: false,
        hasValidSheetSettings: false,
        hasActivePlentyCredential: false,
      };
    }
  };

  const handlePopulateArticles = async () => {
    try {
      setDownloadStatus("running");
      setDownloadProgress(0);
      setDownloadStatusMessage("Initializing population process...");
      setError(null);
      setPopulateLoading(true);
      setPopulateResult(null);

      // Close existing WebSocket connection if any
      if (websocket) {
        console.log("Closing existing WebSocket connection");
        websocket.close();
        setWebsocket(null);
      }

      // Initialize WebSocket connection BEFORE starting the populate request
      const ws = initializeWebSocket();

      // Start the populate process (returns immediately)
      const response = await plentyArticlesApi.populateArticles();

      // The process is now running in the background
      // Progress updates will come via WebSocket
      console.log("Populate process started:", response.data);

      // Don't set populateLoading to false here - let WebSocket completion do it
      // Don't set success message here - let WebSocket completion do it
    } catch (error) {
      console.error("Error starting populate process:", error);
      setError(getErrorMessage(error));
      setDownloadStatus("error");
      setPopulateLoading(false);
    }
  };

  // Cron Job Management functions
  const loadCronStatus = async () => {
    try {
      const response = await plentyArticlesApi.getCronStatus();

      // Normalize the response data to match component expectations
      // Backend returns: status, name, expression, last_run_at, next_run_at, active
      const rawData = response.data;
      const normalizedStatus = {
        ...rawData,
        is_active: rawData.active ?? rawData.is_active,
        cron_expression: rawData.expression ?? rawData.cron_expression,
        last_run: rawData.last_run_at ?? rawData.last_run,
        next_run: rawData.next_run_at ?? rawData.next_run,
      };

      setCronStatus(normalizedStatus);

      if (normalizedStatus.is_active && normalizedStatus.cron_expression) {
        setCronExpression(normalizedStatus.cron_expression);
      }
    } catch (error) {
      console.error("Error loading cron status:", error);
    }
  };

  const handleStartCron = async () => {
    try {
      setCronLoading(true);
      setError(null);

      // Sanitize cron expression if it's an object or wrapped event
      let scheduleToSend = cronExpression;
      if (typeof cronExpression === "object" && cronExpression !== null) {
        // Some versions of react-cron-generator pass an object on initial render or onChange.
        // We should try to extract the string if possible, or fallback to default.
        console.warn(
          "Cron expression is an object, attempting to extract string",
          cronExpression,
        );
        // If we can't extract, default to current expression or a safe default
        scheduleToSend = "0 8 * * *";
      }

      // Also handle if the cron generator produces 6-7 fields (Quartz) but backend expects 5 (Standard)
      // This is a naive check, but helps with compatibility
      // Example Quartz: 0 0/10 * * * ? * -> Standard: */10 * * * *

      console.log("Sending cron schedule:", scheduleToSend);

      const response = await plentyArticlesApi.startCronJob(scheduleToSend);
      setSuccess(response.data.message || "Cron job started successfully!");

      // Reload cron status
      await loadCronStatus();
    } catch (error) {
      console.error("Error starting cron job:", error);
      setError(getErrorMessage(error));
    } finally {
      setCronLoading(false);
    }
  };

  const handleStopCron = async () => {
    try {
      setCronLoading(true);
      setError(null);

      const response = await plentyArticlesApi.stopCronJob();
      setSuccess(response.data.message || "Cron job stopped successfully!");

      // Reload cron status
      await loadCronStatus();
    } catch (error) {
      console.error("Error stopping cron job:", error);
      setError(getErrorMessage(error));
    } finally {
      setCronLoading(false);
    }
  };

  const handleUpdateCronSchedule = async () => {
    try {
      setCronLoading(true);
      setError(null);

      const response =
        await plentyArticlesApi.updateCronSchedule(cronExpression);
      setSuccess(
        response.data.message || "Cron schedule updated successfully!",
      );

      // Reload cron status
      await loadCronStatus();
    } catch (error) {
      console.error("Error updating cron schedule:", error);
      setError(getErrorMessage(error));
    } finally {
      setCronLoading(false);
    }
  };

  // Load cron status on component mount
  useEffect(() => {
    loadCronStatus();
  }, []);

  return (
    <Grid container spacing={3}>
      {/* Left column: Utilities + System Status */}
      <Grid item xs={12} md={4}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Cron Job Management Card */}
          <Card sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6">
                  Population Schedule Control
                </Typography>
              </Box>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Schedule automatic article population using cron expressions.
                  The system will run the populate workflow at specified
                  intervals.
                </Typography>
              </Alert>

              <Grid container spacing={2}>
                {/* Cron Schedule Section */}
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 2, fontWeight: "medium" }}
                  >
                    Automated Population Schedule
                  </Typography>

                  <CronBuilderBox mb={2}>
                    <Cron
                      onChange={(e) => {
                        // Some versions of react-cron-generator emit an event object OR a string
                        // We need to handle both
                        const value =
                          typeof e === "string" ? e : e?.target?.value || e;
                        console.log("Cron value changed:", value);
                        setCronExpression(value);
                      }}
                      value={cronExpression}
                      showResultText={true}
                      showResultCron={true}
                    />
                  </CronBuilderBox>

                  <Box display="flex" gap={1} mt={2} flexWrap="wrap">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleStartCron} // This calls updateCronSchedule logic effectively due to API behavior
                      disabled={cronLoading}
                      startIcon={<PlayArrowIcon />}
                    >
                      Start / Update Schedule
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleStopCron}
                      disabled={cronLoading || !cronStatus?.is_active}
                      startIcon={<StopIcon />}
                    >
                      Stop Schedule
                    </Button>
                    {cronLoading && <CircularProgress size={20} />}
                  </Box>
                </Grid>
              </Grid>

              <CronStatusPanel isActive={cronStatus?.is_active} sx={{ mt: 3 }}>
                <Box
                  display="grid"
                  gridTemplateColumns="repeat(auto-fit, minmax(150px, 1fr))"
                  gap={2}
                >
                  <Box>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                    >
                      Cron Status
                    </Typography>
                    <Typography variant="body1" fontWeight="500">
                      {cronStatus?.is_active ? (
                        <Box
                          component="span"
                          sx={{
                            color: "success.main",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <CheckCircleIcon fontSize="small" /> Active
                        </Box>
                      ) : (
                        <Box
                          component="span"
                          sx={{
                            color: "error.main",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <StopIcon fontSize="small" /> Inactive
                        </Box>
                      )}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                    >
                      Schedule
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}
                    >
                      {cronStatus?.cron_expression || "Not set"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                    >
                      Last Run
                    </Typography>
                    <Typography variant="body2">
                      {cronStatus?.last_run
                        ? new Date(cronStatus.last_run).toLocaleString()
                        : "Never"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                    >
                      Next Execution
                    </Typography>
                    <Typography
                      variant="body2"
                      color={
                        cronStatus?.is_active ? "textPrimary" : "textSecondary"
                      }
                    >
                      {cronStatus?.next_run && cronStatus?.is_active
                        ? new Date(cronStatus.next_run).toLocaleString()
                        : "-"}
                    </Typography>
                  </Box>
                </Box>
              </CronStatusPanel>
            </CardContent>
          </Card>

          {/* System Status Card */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                System Status
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  This service populates Plenty articles from the specified date
                  range, based on the current system configuration. Ensure all
                  systems are configured before proceeding.
                </Typography>
              </Alert>
              <List>
                <ListItem>
                  <ListItemIcon>
                    {systemStatus.hasActiveGoogleCredential ? (
                      <CheckCircleIcon sx={{ color: "success.main" }} />
                    ) : (
                      <ErrorIcon sx={{ color: "error.main" }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary="Google Credentials"
                    secondary={
                      systemStatus.hasActiveGoogleCredential
                        ? "Active and authorized credential found"
                        : "No active or authorized credentials"
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    {systemStatus.hasValidSheetSettings ? (
                      <CheckCircleIcon sx={{ color: "success.main" }} />
                    ) : (
                      <ErrorIcon sx={{ color: "error.main" }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary="Sheets Settings"
                    secondary={
                      systemStatus.hasValidSheetSettings
                        ? "Valid sheet configuration found"
                        : "Sheet settings incomplete"
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    {systemStatus.hasActivePlentyCredential ? (
                      <CheckCircleIcon sx={{ color: "success.main" }} />
                    ) : (
                      <ErrorIcon sx={{ color: "error.main" }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary="Plenty Credentials"
                    secondary={
                      systemStatus.hasActivePlentyCredential
                        ? "Active and authorized credential found"
                        : "No active or authorized credentials"
                    }
                  />
                </ListItem>
              </List>
              {systemStatus.isReady ? (
                <Alert severity="success" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    ✅ System is ready for article population!
                  </Typography>
                </Alert>
              ) : (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    ⚠️ Please complete the configuration in other tabs before
                    starting article population.
                  </Typography>
                </Alert>
              )}
            </CardContent>
          </Card>
        </Box>
      </Grid>

      {/* Right column: Rules + Populate */}
      <Grid item xs={12} md={8}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Populate Articles
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                Populate Articles fetches items from the Plenty API in the
                chosen date range and enriches each item with current stock and
                recent sales volume.
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                It then computes a stock-to-sales ratio and updates the database
                (creates new articles or updates existing ones).
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                After the DB update the workflow will attempt to update the
                Google Sheets and apply the current categorizations.
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={handlePopulateArticles}
                      disabled={
                        populateLoading ||
                        downloadStatus === "running" ||
                        !systemStatus.isReady
                      }
                      startIcon={
                        populateLoading || downloadStatus === "running" ? (
                          <CircularProgress size={20} />
                        ) : (
                          <AddIcon />
                        )
                      }
                      fullWidth
                      sx={{ height: "56px" }}
                    >
                      {populateLoading || downloadStatus === "running"
                        ? "Populating..."
                        : "Populate Articles"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>

              {populateResults && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Population Results
                  </Typography>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      Successfully populated {populateResults.totalItems}{" "}
                      articles ({populateResults.newItems} new,{" "}
                      {populateResults.updatedItems} updated)
                    </Typography>
                  </Alert>
                  {populateResults.errors &&
                    populateResults.errors.length > 0 && (
                      <Alert severity="warning">
                        <Typography variant="body2">
                          {populateResults.errors.length} errors occurred during
                          population
                        </Typography>
                      </Alert>
                    )}
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Download Statistics */}
          {(downloadStatus === "running" || downloadStats.total > 0) && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Download Statistics
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: "center" }}>
                      <Typography variant="h4" color="primary.main">
                        {downloadStats.total}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total Items
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: "center" }}>
                      <Typography variant="h4" color="info.main">
                        {downloadStats.processed}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Processed
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: "center" }}>
                      <Typography variant="h4" color="success.main">
                        {downloadStats.successful}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Successful
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: "center" }}>
                      <Typography variant="h4" color="error.main">
                        {downloadStats.failed}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Failed
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {(downloadStatus === "running" || downloadProgress > 0) && (
                  <Box sx={{ mt: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Progress
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {Math.round(downloadProgress)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={downloadProgress}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                )}

                {downloadStats.currentStep && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Current Step: <strong>{downloadStats.currentStep}</strong>
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export default PopulateArticlesTab;
